import type { FormInstance } from '@arco-design/web-react/es/Form';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { MaasInferenceSource } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import type { ContainerDeployHealthCheckConfig } from '@/delivery-type-plugin/capabilities/maas-system-config/deployConfigPayload';
import { healthCheckToK8sLivenessProbe } from '@/delivery-type-plugin/capabilities/maas-system-config/deployConfigPayload';
import { SKU_RESOURCE_MODULES_FIELD, type SkuResourceModulesState } from '@/delivery-type-plugin/platform/scene-layout/types';
import { safeParseJSON } from '@/utils/json-parse';

import { DEFAULT_HEALTH_CHECK } from './constants';
import { HEALTH_CHECK_FIELD } from './fieldKeys';
import type { HealthCheckDeployType, HealthCheckFormValues, ProbeType } from './types';

export { HEALTH_CHECK_FIELD };

function normalizeProbeTypes(raw: unknown): ProbeType[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const types = raw.map((item) => String(item).trim().toLowerCase()).filter(Boolean) as ProbeType[];
  return types.length ? types : undefined;
}

/** 从 containerDeployConfig.healthCheck 解析为表单值 */
export function parseHealthCheckFromDeployConfig(
  healthCheck?: ContainerDeployHealthCheckConfig | Record<string, unknown> | null,
): HealthCheckFormValues | undefined {
  if (!healthCheck || typeof healthCheck !== 'object') return undefined;
  const hc = healthCheck as ContainerDeployHealthCheckConfig;
  return {
    ...DEFAULT_HEALTH_CHECK,
    enabled: hc.enabled === true,
    supportedProbeTypes: normalizeProbeTypes(hc.supportedProbeTypes) ?? DEFAULT_HEALTH_CHECK.supportedProbeTypes,
    defaultProbeType: (String(hc.defaultProbeType ?? 'http')
      .trim()
      .toLowerCase() || 'http') as ProbeType,
    defaultPort: Number.isFinite(Number(hc.defaultPort)) ? Number(hc.defaultPort) : DEFAULT_HEALTH_CHECK.defaultPort,
    defaultPath: String(hc.defaultPath ?? '').trim() || DEFAULT_HEALTH_CHECK.defaultPath,
    httpMethod: String(hc.httpMethod ?? 'GET').trim() || 'GET',
    defaultCommand: String(hc.defaultCommand ?? ''),
    initialDelaySeconds: Number.isFinite(Number(hc.initialDelaySeconds))
      ? Number(hc.initialDelaySeconds)
      : DEFAULT_HEALTH_CHECK.initialDelaySeconds,
    periodSeconds: Number.isFinite(Number(hc.periodSeconds)) ? Number(hc.periodSeconds) : DEFAULT_HEALTH_CHECK.periodSeconds,
    timeoutSeconds: Number.isFinite(Number(hc.timeoutSeconds)) ? Number(hc.timeoutSeconds) : DEFAULT_HEALTH_CHECK.timeoutSeconds,
    failureThreshold: Number.isFinite(Number(hc.failureThreshold)) ? Number(hc.failureThreshold) : DEFAULT_HEALTH_CHECK.failureThreshold,
  };
}

/** 镜像是否预置了可展示的探活配置 */
export function hasMeaningfulHealthCheckConfig(healthCheck?: HealthCheckFormValues): boolean {
  if (!healthCheck) return false;
  if (healthCheck.enabled) return true;
  const probeType = String(healthCheck.defaultProbeType ?? '')
    .trim()
    .toLowerCase();
  if (probeType === 'exec') return Boolean(String(healthCheck.defaultCommand ?? '').trim());
  const port = Number(healthCheck.defaultPort);
  return Number.isFinite(port) && port > 0;
}

/** 探活模块是否启用（由镜像 healthCheck.enabled 决定，非用户手动勾选） */
export function isHealthCheckEnabled(healthCheck?: HealthCheckFormValues | null): boolean {
  return healthCheck?.enabled === true;
}

/** 将 skuResourceModules.healthCheck 与镜像 enabled 对齐 */
export function syncHealthCheckSkuModuleFromForm(form: FormInstance, enabledOverride?: boolean): void {
  const enabled = enabledOverride ?? isHealthCheckEnabled(readHealthCheckFromFormValues(form.getFieldsValue()));
  const current = form.getFieldValue(SKU_RESOURCE_MODULES_FIELD) as SkuResourceModulesState | undefined;
  if (!current || typeof current !== 'object') {
    form.setFieldValue(SKU_RESOURCE_MODULES_FIELD, { healthCheck: enabled });
    return;
  }
  if (current.healthCheck === enabled) return;
  form.setFieldValue(SKU_RESOURCE_MODULES_FIELD, { ...current, healthCheck: enabled });
}

/** 选镜像后写入探活表单字段 */
export function applyHealthCheckFromDeployConfig(
  form: FormInstance,
  config?: Record<string, unknown> | null,
  options?: { systemLivenessProbeField?: string; field?: string },
) {
  const field = options?.field ?? HEALTH_CHECK_FIELD;
  const parsed = parseHealthCheckFromDeployConfig(config?.healthCheck as ContainerDeployHealthCheckConfig | undefined);
  if (!parsed) {
    form.setFieldValue(field, undefined);
    syncHealthCheckSkuModuleFromForm(form, false);
    if (options?.systemLivenessProbeField) {
      form.setFieldValue(options.systemLivenessProbeField, undefined);
    }
    return;
  }
  form.setFieldValue(field, parsed);
  syncHealthCheckSkuModuleFromForm(form, parsed.enabled === true);
  if (options?.systemLivenessProbeField) {
    const probe = healthCheckToK8sLivenessProbe(parsed);
    form.setFieldValue(options.systemLivenessProbeField, probe);
  }
}

/** 清空探活相关表单字段 */
export function clearHealthCheckFromForm(form: FormInstance, options?: { systemLivenessProbeField?: string; field?: string }) {
  form.setFieldValue(options?.field ?? HEALTH_CHECK_FIELD, undefined);
  syncHealthCheckSkuModuleFromForm(form, false);
  if (options?.systemLivenessProbeField) {
    form.setFieldValue(options.systemLivenessProbeField, undefined);
  }
}

/** 从表单读取探活配置 */
export function readHealthCheckFromFormValues(
  values: Record<string, unknown>,
  field = HEALTH_CHECK_FIELD,
): HealthCheckFormValues | undefined {
  const raw = values[field];
  if (!raw || typeof raw !== 'object') return undefined;
  return { ...DEFAULT_HEALTH_CHECK, ...(raw as HealthCheckFormValues) };
}

/** embedded 节点提交：用户填写有效探活时视为 enabled */
export function resolveEmbeddedHealthCheckForSubmit(healthCheck?: HealthCheckFormValues | null): HealthCheckFormValues | undefined {
  if (!healthCheck) return undefined;
  if (hasMeaningfulHealthCheckConfig(healthCheck)) {
    return { ...healthCheck, enabled: true };
  }
  return healthCheck.enabled === true ? healthCheck : undefined;
}

/** 提交前：将表单探活合并进 containerDeployConfig；镜像 enabled 为 false 时不写入 healthCheck */
export function mergeHealthCheckIntoContainerDeployConfig(
  config: Record<string, unknown> | undefined,
  values: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!config) return config;
  const next = { ...config };
  const healthCheck = readHealthCheckFromFormValues(values);
  if (!isHealthCheckEnabled(healthCheck)) {
    delete next.healthCheck;
    return next;
  }
  if (!healthCheck) {
    delete next.healthCheck;
    return next;
  }
  const supportedProbeTypes = healthCheck.supportedProbeTypes ?? normalizeProbeTypes(config.healthCheck);
  next.healthCheck = {
    enabled: healthCheck.enabled === true,
    ...(supportedProbeTypes?.length ? { supportedProbeTypes } : {}),
    defaultProbeType: healthCheck.defaultProbeType ?? 'http',
    ...(healthCheck.defaultPort != null ? { defaultPort: healthCheck.defaultPort } : {}),
    ...(healthCheck.defaultPath ? { defaultPath: healthCheck.defaultPath } : {}),
    ...(healthCheck.httpMethod ? { httpMethod: healthCheck.httpMethod } : {}),
    ...(healthCheck.defaultCommand ? { defaultCommand: healthCheck.defaultCommand } : {}),
    ...(healthCheck.initialDelaySeconds != null ? { initialDelaySeconds: healthCheck.initialDelaySeconds } : {}),
    ...(healthCheck.periodSeconds != null ? { periodSeconds: healthCheck.periodSeconds } : {}),
    ...(healthCheck.timeoutSeconds != null ? { timeoutSeconds: healthCheck.timeoutSeconds } : {}),
    ...(healthCheck.failureThreshold != null ? { failureThreshold: healthCheck.failureThreshold } : {}),
  };
  return next;
}

function readMaasInferenceFrameworkImage(values: Record<string, unknown>): CommonImageItemType | undefined {
  const rawImage = values.inferenceFrameworkImage;
  if (!rawImage) return undefined;
  if (typeof rawImage === 'string') {
    return safeParseJSON(rawImage, undefined) as CommonImageItemType | undefined;
  }
  return rawImage as CommonImageItemType;
}

/** 是否展示探活 SKU/下单模块（平台内置镜像已选且镜像 enable 为 true） */
export function shouldShowHealthCheckModule(values: Record<string, unknown>, deployType: HealthCheckDeployType): boolean {
  if (!isBuiltinImageSelected(values, deployType)) return false;
  return isHealthCheckEnabled(readHealthCheckFromFormValues(values));
}

/** 是否选中平台内置镜像（控制探活模块可见性） */
export function isBuiltinImageSelected(values: Record<string, unknown>, deployType: HealthCheckDeployType): boolean {
  if (deployType === 'ContainerInstance') {
    const imageKey = values.basic && typeof values.basic === 'object' ? (values.basic as Record<string, unknown>).image : undefined;
    const key =
      imageKey && typeof imageKey === 'object'
        ? String((imageKey as Record<string, unknown>).key ?? '').trim()
        : String(values['basic.image.key'] ?? '').trim();
    const url =
      imageKey && typeof imageKey === 'object'
        ? String((imageKey as Record<string, unknown>).url ?? '').trim()
        : String(values['basic.image.url'] ?? '').trim();
    return Boolean(key || url);
  }

  const inferenceSource = String(values.maasInferenceSource ?? '').trim();
  if (inferenceSource === MaasInferenceSource.Custom) return false;
  if (inferenceSource === MaasInferenceSource.Builtin) {
    return Boolean(readMaasInferenceFrameworkImage(values));
  }
  const imageItem = readMaasInferenceFrameworkImage(values);
  if (imageItem) return true;
  const inferenceSystem = parseFormJsonField<Record<string, unknown>>(values._inferenceSystem);
  const imageKey = String(inferenceSystem?.imageKey ?? '').trim();
  const imageUrl = String(inferenceSystem?.url ?? inferenceSystem?.image ?? '').trim();
  return Boolean(imageKey || imageUrl);
}

/** livenessProbe 反推最小 healthCheck（编辑回填兜底） */
export function healthCheckFromLivenessProbe(probe?: Record<string, unknown> | null): HealthCheckFormValues | undefined {
  if (!probe || typeof probe !== 'object') return undefined;
  if (probe.exec && typeof probe.exec === 'object') {
    const command = (probe.exec as { command?: unknown }).command;
    const commandText = Array.isArray(command) ? command.map(String).join(' ') : String(command ?? '');
    return {
      ...DEFAULT_HEALTH_CHECK,
      enabled: true,
      defaultProbeType: 'exec',
      defaultCommand: commandText,
      initialDelaySeconds: Number(probe.initialDelaySeconds) || DEFAULT_HEALTH_CHECK.initialDelaySeconds,
      periodSeconds: Number(probe.periodSeconds) || DEFAULT_HEALTH_CHECK.periodSeconds,
      timeoutSeconds: Number(probe.timeoutSeconds) || DEFAULT_HEALTH_CHECK.timeoutSeconds,
      failureThreshold: Number(probe.failureThreshold) || DEFAULT_HEALTH_CHECK.failureThreshold,
    };
  }
  if (probe.tcpSocket && typeof probe.tcpSocket === 'object') {
    const port = Number((probe.tcpSocket as { port?: unknown }).port);
    return {
      ...DEFAULT_HEALTH_CHECK,
      enabled: true,
      defaultProbeType: 'tcp',
      defaultPort: Number.isFinite(port) ? port : DEFAULT_HEALTH_CHECK.defaultPort,
      initialDelaySeconds: Number(probe.initialDelaySeconds) || DEFAULT_HEALTH_CHECK.initialDelaySeconds,
      periodSeconds: Number(probe.periodSeconds) || DEFAULT_HEALTH_CHECK.periodSeconds,
      timeoutSeconds: Number(probe.timeoutSeconds) || DEFAULT_HEALTH_CHECK.timeoutSeconds,
      failureThreshold: Number(probe.failureThreshold) || DEFAULT_HEALTH_CHECK.failureThreshold,
    };
  }
  if (probe.httpGet && typeof probe.httpGet === 'object') {
    const httpGet = probe.httpGet as { port?: unknown; path?: unknown; scheme?: unknown };
    const scheme = String(httpGet.scheme ?? 'HTTP').toUpperCase();
    return {
      ...DEFAULT_HEALTH_CHECK,
      enabled: true,
      defaultProbeType: 'http',
      defaultPort: Number(httpGet.port) || DEFAULT_HEALTH_CHECK.defaultPort,
      defaultPath: String(httpGet.path ?? '').trim() || DEFAULT_HEALTH_CHECK.defaultPath,
      httpMethod: scheme === 'HTTPS' ? 'HTTPS' : 'GET',
      initialDelaySeconds: Number(probe.initialDelaySeconds) || DEFAULT_HEALTH_CHECK.initialDelaySeconds,
      periodSeconds: Number(probe.periodSeconds) || DEFAULT_HEALTH_CHECK.periodSeconds,
      timeoutSeconds: Number(probe.timeoutSeconds) || DEFAULT_HEALTH_CHECK.timeoutSeconds,
      failureThreshold: Number(probe.failureThreshold) || DEFAULT_HEALTH_CHECK.failureThreshold,
    };
  }
  return undefined;
}
